import React, {
  createContext,
  useCallback,
  useReducer,
  ReactNode,
} from "react";

export type ScopeType = "root" | "group" | "scope" | "unscoped";

export interface ScopesStateType {
  scopesList?: ScopeNode[] | null;
  selectedScope?: SelectedScope | null;
  defaultScope?: SelectedScope | null;
  scopeToExecute?: string | null;
  pendingScopeToExecute?: string | null;
  isNewScope?: boolean;
  isDirty?: boolean;
  shouldBlinkField?: string;
  unscoped?: SelectedScope | null;
  shouldOpenSelector?: boolean;
}

export interface SelectedScope {
  value: {
    id?: string;
    query?: string;
    editable?: boolean;
    type?: ScopeType;
    isDirty?: boolean;
    children?: SelectedScope[];
  };
  children?: SelectedScope[];
  filter_data?: string;
  label?: string;
  parentId?: string | null;
  level?: number;
}

export interface ScopeNode {
  id: string;
  name: string;
  type: ScopeType;
  editable: boolean;
  children?: ScopeNode[];
  filter_data?: any;
  created_by?: string | null;
  has_scope_descendants?: boolean;
}

export interface ScopesContextType {
  scopesState: ScopesStateType;
  updateScopeState: (state: ScopesStateType | Partial<ScopesStateType>) => void;
  getScopeValueById: (
    scopeId: string,
    scopesListRoot?: ScopeNode
  ) => SelectedScope | null;
  searchTree: (node: ScopeNode, id: string) => ScopeNode | null;
}

interface ScopeProviderProps {
  children: ReactNode;
}

export const scopesInitialValues: ScopesContextType = {
  scopesState: {
    scopesList: null,
    selectedScope: null,
    defaultScope: null,
    scopeToExecute: null,
    pendingScopeToExecute: null,
    isNewScope: false,
    isDirty: false,
    shouldBlinkField: undefined,
    unscoped: null,
  },
  updateScopeState: () => {},
  getScopeValueById: () => null,
  searchTree: () => null,
};

export const ScopesContext =
  createContext<ScopesContextType>(scopesInitialValues);

export const searchTree = (node: ScopeNode, id: string): ScopeNode | null => {
  if (node?.id?.toString() === id) {
    return node;
  } else if (node?.children?.length) {
    let result: ScopeNode | null = null;
    for (let i = 0; result === null && i < node?.children?.length; i++) {
      result = searchTree(node.children[i], id);
    }
    return result;
  }
  return null;
};

export const getScopeValueFromTree = (
  scopeId: string,
  scopesListRoot: ScopeNode | undefined
): SelectedScope | null => {
  if (!scopesListRoot) {
    return null;
  }

  const scope = searchTree(scopesListRoot, scopeId);

  if (!scope) {
    return null;
  }

  const selectedScope: SelectedScope = {
    label: scope.name,
    value: {
      id: scope.id,
      editable: scope.editable,
      type: scope.type,
      children: scope.children
        ? scope.children.map((child) => ({
            label: child.name,
            has_scope_descendants: child.has_scope_descendants,
            value: {
              id: child.id,
              editable: child.editable,
              type: child.type,
            },
          }))
        : undefined,
    },
  };

  if (scope.type === "group" && scope.children) {
    selectedScope.value.children = scope.children.map((child) => ({
      label: child.name,
      has_scope_descendants: child.has_scope_descendants,
      type: child.type,
      value: {
        id: child.id,
        editable: child.editable,
        type: child.type,
      },
    }));
  }

  if (scope.type === "scope") {
    selectedScope.value.query = JSON.stringify(scope.filter_data);
  }

  return selectedScope;
};

const ScopeProvider: React.FC<ScopeProviderProps> = ({ children }) => {
  const [scopesState, updateScopeState] = useReducer(
    (state: ScopesStateType, newState: Partial<ScopesStateType>) => ({
      ...state,
      ...newState,
    }),
    scopesInitialValues.scopesState
  );

  const getScopeValueById = useCallback(
    (
      scopeId: string,
      scopesListRoot: ScopeNode | undefined = scopesState?.scopesList?.[0]
    ): SelectedScope | null => getScopeValueFromTree(scopeId, scopesListRoot),
    [scopesState?.scopesList]
  );

  const scopesContextValue = React.useMemo<ScopesContextType>(
    () => ({
      scopesState,
      updateScopeState,
      getScopeValueById,
      searchTree,
    }),
    [scopesState, getScopeValueById]
  );

  return (
    <ScopesContext.Provider value={scopesContextValue}>
      {children}
    </ScopesContext.Provider>
  );
};

export default ScopeProvider;
